import {convertToURLDatetime, parseStartEndDatetimeFromURLParams} from '@domain/shared'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {
  adjustRangeByMinMaxLimitAndBoundary,
  calculateDatetimeRangeWithinBoundary
} from '@widgets/planning-schedule-chart'
import React, {useMemo, useLayoutEffect, useCallback} from 'react'

import {LayoutDailyPlanning, MIN_RANGE_HOURS, MAX_RANGE_HOURS} from '../../components'
import {ProductionPlanningSkeleton} from '../../components/shared/skeletons'
import {LONG_TERM_PLANNING_DAYS, ProductionPlanningLSSettingsKeys} from '../../consts'
import {
  SelectedTimeframeProvider,
  useLocalStorageSettings,
  useLongTermPlanBoundary,
  usePlanRange,
  useSearchParams
} from '../../hooks'
import {DialogProvider} from '../../providers'
import type {MomentRange} from '../../types'

const getDailyTimeframeFromSelectedRange = (
  range: MomentRange,
  timezoneId: string
): MomentRange => {
  const startOfTimeframe = range[0].clone().tz(timezoneId).startOf('day')
  return [startOfTimeframe, startOfTimeframe.clone().add(LONG_TERM_PLANNING_DAYS, 'days')]
}

export const PageDailyProductionPlanning: React.FC = () => {
  const timezoneId = useUpmPlantTimezone()
  // boundary to set limits of scroll back and forward
  const boundary = useLongTermPlanBoundary()

  // we need to infer full timeframe from a selected range from URL
  // TODO abstract it to hook or fn getTimeFrameFromUrlParams
  const [obtainedParams, setSearchParams] = useSearchParams('start', 'end')

  const parsedSelectedRange = useMemo(() => {
    const selectedRange = parseStartEndDatetimeFromURLParams({...obtainedParams, timezoneId})
    const adjustedRange = selectedRange
      ? adjustRangeByMinMaxLimitAndBoundary(selectedRange, boundary, [
          MIN_RANGE_HOURS,
          MAX_RANGE_HOURS
        ])
      : undefined
    return adjustedRange
  }, [obtainedParams, boundary, timezoneId])

  const defaultTimeframe = usePlanRange({days: LONG_TERM_PLANNING_DAYS})

  // get timeframe from url at this point, if not exist, use default timeframe
  const timeframe = parsedSelectedRange
    ? calculateDatetimeRangeWithinBoundary(
        getDailyTimeframeFromSelectedRange(parsedSelectedRange, timezoneId),
        boundary
      )
    : defaultTimeframe

  // slider default range
  const {settings: sliderSettings, updateSettings: updateSliderSetting} = useLocalStorageSettings(
    ProductionPlanningLSSettingsKeys.PlanningSliderSettings,
    {defaultRange: 24}
  )

  const sliderDefaultRange = sliderSettings.defaultRange

  const updateSliderDefaultRange = useCallback(
    (defaultRange: number) => {
      updateSliderSetting({defaultRange})
    },
    [updateSliderSetting]
  )

  // if selectedRange is not set, set it to default selected range
  useLayoutEffect(() => {
    if (!parsedSelectedRange) {
      setSearchParams({
        start: convertToURLDatetime(timeframe[0], timezoneId),
        end: convertToURLDatetime(timeframe[0].clone().add(sliderDefaultRange, 'hours'), timezoneId)
      })
    }
  }, [parsedSelectedRange, setSearchParams, timeframe, sliderDefaultRange, timezoneId])

  if (!parsedSelectedRange) {
    return <ProductionPlanningSkeleton height="40vh" />
  }

  return (
    <DialogProvider>
      <SelectedTimeframeProvider defaultTimeframe={timeframe}>
        <LayoutDailyPlanning
          updateSliderDefaultRange={updateSliderDefaultRange}
          sliderDefaultRange={sliderDefaultRange}
          boundary={boundary}
        />
      </SelectedTimeframeProvider>
    </DialogProvider>
  )
}
